import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Footprints, Play, Square, Loader2, Lock, Crown, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { recordWalkPoint, fetchWalkMap, resolveWalkPlaces } from '../lib/walkLocation';
import { announceWalk } from '../hooks/useWalkForegroundPoints';
import { WalkMap, placesText } from './WalkMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Controllo passeggiata attiva dal dashboard: solo mentre una passeggiata è in corso
const ACTIVE_WALK_POLL_MS = 30000;
// Punti alla partenza e al rientro; quelli intermedi li registra useWalkForegroundPoints a livello di app
const START_FIX_TIMEOUT_MS = 15000;
const END_FIX_TIMEOUT_MS = 10000;

const formatTime = (iso) => new Date(iso).toLocaleTimeString('it-IT', {
  hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome'
});

const formatDay = (iso) => {
  const date = new Date(iso);
  const dayKey = (d) => d.toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' });
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  if (dayKey(date) === dayKey(today)) return 'oggi';
  if (dayKey(date) === dayKey(yesterday)) return 'ieri';
  return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', timeZone: 'Europe/Rome' });
};

const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
};

const walkSummary = (walk) => {
  const range = `${formatDay(walk.started_at)} ${formatTime(walk.started_at)}–${walk.ended_at ? formatTime(walk.ended_at) : '?'}`;
  return walk.duration_minutes == null ? `${range} · Non terminata` : `${range} · ${walk.duration_minutes} min`;
};

// Timer che conta dall'orario di inizio salvato sul server
const useElapsed = (startedAt) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!startedAt) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return startedAt ? formatElapsed(now - new Date(startedAt).getTime()) : null;
};

// Ricontrolla quando la pagina torna visibile
const useOnVisible = (callback) => {
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') callback();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [callback]);
};

const PulsingDot = () => (
  <span className="relative flex h-2.5 w-2.5">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
  </span>
);

const WalkPremiumLock = () => (
  <div className="rounded-xl p-4 border-2 border-gray-200 bg-gray-50 opacity-75" data-testid="walk-premium-lock">
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gray-200">
        <Footprints className="w-6 h-6 text-gray-400" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-gray-500">Passeggiata</p>
          <span className="px-2 py-0.5 bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white text-[10px] font-bold rounded-full flex items-center gap-1">
            <Crown className="w-3 h-3" />
            PREMIUM
          </span>
        </div>
        <p className="text-sm text-gray-400">Il registro delle passeggiate è disponibile solo con il piano Premium</p>
      </div>
      <Lock className="w-5 h-5 text-gray-400" />
    </div>
  </div>
);

// App assistente: Avvia / Stop, stato ripreso dal server all'apertura e al ritorno sulla scheda
export const GPSTracker = ({ token, famigliaId, isPremium, onSessionChange, onWalkStopped }) => {
  const [walk, setWalk] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const lastWalkKey = `walk_last_${famigliaId}`;
  const [lastWalk, setLastWalk] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(lastWalkKey));
    } catch (e) {
      return null;
    }
  });
  const elapsed = useElapsed(walk?.started_at);

  const applyWalk = useCallback((next) => {
    setWalk(next);
    if (onSessionChange) onSessionChange(Boolean(next));
  }, [onSessionChange]);

  const loadActiveWalk = useCallback(async () => {
    if (!isPremium || !famigliaId || !token) return;
    try {
      const res = await fetch(`${API_URL}/api/gps/active/${famigliaId}`, {
        headers: { 'X-Family-Token': token }
      });
      if (!res.ok) return null;
      const data = await res.json();
      const session = data.active_session;
      applyWalk(session ? { id: session.id, started_at: session.started_at } : null);
      return session ? session.id : null;
    } catch (err) {
      console.error('[Walk] active check error:', err);
      return null;
    }
  }, [isPremium, famigliaId, token, applyWalk]);

  useEffect(() => {
    loadActiveWalk();
  }, [loadActiveWalk]);
  useOnVisible(loadActiveWalk);

  const runOnce = async (action) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await action();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const startWalk = () => runOnce(async () => {
    try {
      const res = await fetch(`${API_URL}/api/gps/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Family-Token': token },
        body: JSON.stringify({ famiglia_id: famigliaId, assistente_nome: 'Assistente' })
      });
      if (!res.ok) throw new Error('start failed');
      const data = await res.json();
      applyWalk({ id: data.session_id, started_at: data.started_at });
      announceWalk(data.session_id);
      // La posizione non blocca mai la passeggiata: richiesta in parallelo, senza avvisi se manca
      recordWalkPoint({
        sessionId: data.session_id, token, kind: data.already_active ? 'during' : 'start',
        timeoutMs: START_FIX_TIMEOUT_MS, allowPrompt: true
      });
      toast.success(data.already_active ? 'Passeggiata già in corso: riprendi da qui' : 'Passeggiata iniziata');
    } catch (err) {
      console.error('[Walk] start error:', err);
      toast.error('Impossibile avviare la passeggiata, riprova');
    }
  });

  // Dopo lo Stop già salvato: punto finale entro 10 s, poi i nomi dei luoghi salvati con la passeggiata
  const finishWalkLocation = async (sessionId) => {
    await recordWalkPoint({ sessionId, token, kind: 'end', timeoutMs: END_FIX_TIMEOUT_MS });
    const map = await fetchWalkMap(sessionId, token);
    if (map?.pins?.length) await resolveWalkPlaces({ sessionId, token, pins: map.pins });
  };

  const stopWalk = () => runOnce(async () => {
    try {
      const res = await fetch(`${API_URL}/api/gps/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Family-Token': token },
        body: JSON.stringify({ session_id: walk.id })
      });
      if (!res.ok) throw new Error('stop failed');
      const data = await res.json();
      const finished = { started_at: data.started_at, ended_at: data.ended_at, duration_minutes: data.duration_minutes };
      localStorage.setItem(lastWalkKey, JSON.stringify(finished));
      setLastWalk(finished);
      applyWalk(null);
      announceWalk(null);
      if (onWalkStopped) onWalkStopped(data.session_id);
      finishWalkLocation(data.session_id);
      toast.success(data.duration_minutes == null ? 'Passeggiata terminata' : `Passeggiata terminata · ${data.duration_minutes} min`);
    } catch (err) {
      console.error('[Walk] stop error:', err);
      toast.error('Impossibile terminare la passeggiata, riprova');
    }
  });

  if (!isPremium) return <WalkPremiumLock />;

  const running = Boolean(walk);

  return (
    <div className={`rounded-xl p-4 border-2 transition-colors ${
      running ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'
    }`} data-testid="walk-tracker">
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${running ? 'bg-green-500' : 'bg-green-100'}`}>
          <Footprints className={`w-6 h-6 ${running ? 'text-white' : 'text-green-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          {running ? (
            <>
              <p className="font-semibold text-green-700 flex items-center gap-2">
                <PulsingDot />
                Passeggiata in corso
              </p>
              <p className="text-sm text-gray-500" data-testid="walk-tracker-started">Iniziata alle {formatTime(walk.started_at)}</p>
            </>
          ) : (
            <>
              <p className="font-semibold text-gray-700">Passeggiata</p>
              <p className="text-sm text-gray-500">Tocca Avvia quando uscite e Stop al rientro</p>
            </>
          )}
        </div>

        <Button
          onClick={running ? stopWalk : startWalk}
          disabled={busy}
          data-testid="gps-tracking-toggle"
          className={running
            ? 'bg-red-500 hover:bg-red-600 text-white'
            : 'bg-green-500 hover:bg-green-600 text-white'
          }
          size="sm"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : running ? (
            <>
              <Square className="w-4 h-4 mr-1" />
              Stop
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-1" />
              Avvia
            </>
          )}
        </Button>
      </div>

      {running ? (
        <p className="mt-3 text-center text-3xl font-bold tabular-nums text-green-700" data-testid="walk-tracker-timer">{elapsed}</p>
      ) : lastWalk?.started_at ? (
        <p className="mt-3 text-xs text-gray-400" data-testid="walk-tracker-last">Ultima: {walkSummary(lastWalk)}</p>
      ) : null}
    </div>
  );
};

// Dashboard famiglia: stato passeggiata e storico
const withCaptions = (pins) => pins.map((pin) => ({
  ...pin, caption: pin.label ? `${pin.label} ${formatTime(pin.time)}` : formatTime(pin.time)
}));

// Mappa di una passeggiata conclusa: i nomi mancanti vengono risolti e salvati alla prima apertura
const WalkHistoryMap = ({ item, token, onPlacesSaved }) => {
  const pins = item.pins || [];
  useEffect(() => {
    if (!token || !pins.some((p) => (p.label === 'Partenza' || p.label === 'Rientro') && !p.place)) return;
    resolveWalkPlaces({ sessionId: item.id, token, pins }).then((saved) => {
      if (saved?.pins) onPlacesSaved(item.id, saved.pins);
    });
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const captioned = useMemo(() => withCaptions(pins), [pins]);
  const places = placesText(pins);
  return (
    <div className="pb-3 space-y-2" data-testid={`walk-history-map-${item.id}`}>
      <WalkMap pins={captioned} zone={item.zone} testId={`walk-map-${item.id}`} />
      {places && <p className="text-sm text-[#2C3E50]" data-testid={`walk-places-${item.id}`}>{places}</p>}
    </div>
  );
};

export const WalkStatusCard = ({ famiglia }) => {
  const famigliaId = famiglia?.id;
  const token = famiglia?.token;
  const [activeWalk, setActiveWalk] = useState(null);
  const [activeStart, setActiveStart] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [openWalkId, setOpenWalkId] = useState(null);
  const wasActive = useRef(false);
  const elapsed = useElapsed(activeWalk?.started_at);

  const loadHistory = useCallback(async () => {
    if (!famigliaId) return;
    try {
      const res = await fetch(`${API_URL}/api/gps/history/${famigliaId}?limit=10`, {
        headers: token ? { 'X-Family-Token': token } : {}
      });
      if (!res.ok) return;
      const data = await res.json();
      setHistory(data.sessions || []);
      setActiveStart(data.active_start || null);
    } catch (err) {
      console.error('[Walk] history error:', err);
    }
  }, [famigliaId, token]);

  const savePlaces = useCallback((id, pins) => {
    setHistory((items) => items.map((item) => (item.id === id ? { ...item, pins } : item)));
  }, []);

  const loadActiveWalk = useCallback(async () => {
    if (!famigliaId) return;
    try {
      const res = await fetch(`${API_URL}/api/gps/active/${famigliaId}`);
      if (!res.ok) return;
      setActiveWalk((await res.json()).active_session || null);
    } catch (err) {
      console.error('[Walk] active check error:', err);
    }
  }, [famigliaId]);

  useEffect(() => {
    loadActiveWalk();
    loadHistory();
  }, [loadActiveWalk, loadHistory]);
  useOnVisible(loadActiveWalk);

  // Nessun controllo a riposo; ogni 30 secondi solo mentre una passeggiata è in corso
  // La posizione arriva solo dallo storico: la risposta "in corso" non ne contiene mai
  const isActive = Boolean(activeWalk?.id);
  const startPin = activeStart && activeStart.session_id === activeWalk?.id ? activeStart : null;
  const hasStartPin = Boolean(startPin);
  useEffect(() => {
    if (!isActive) return undefined;
    const id = setInterval(() => {
      loadActiveWalk();
      if (!hasStartPin) loadHistory();
    }, ACTIVE_WALK_POLL_MS);
    return () => clearInterval(id);
  }, [isActive, hasStartPin, loadActiveWalk, loadHistory]);

  // Passeggiata iniziata o terminata: ricarica lo storico una volta
  useEffect(() => {
    if (wasActive.current !== isActive) loadHistory();
    wasActive.current = isActive;
  }, [isActive, loadHistory]);
  const runningPins = useMemo(() => (startPin ? withCaptions([startPin]) : []), [startPin]);

  // A riposo la passeggiata viene notata dalla notifica "Passeggiata iniziata" con il dashboard aperto
  useEffect(() => {
    if (!window.OneSignalDeferred) return undefined;
    let oneSignal = null;
    const onForeground = (event) => {
      const type = event?.notification?.additionalData?.type;
      if (type === 'walk_started' || type === 'walk_completed') loadActiveWalk();
    };
    window.OneSignalDeferred.push((OneSignal) => {
      oneSignal = OneSignal;
      OneSignal.Notifications.addEventListener('foregroundWillDisplay', onForeground);
    });
    return () => {
      if (oneSignal) oneSignal.Notifications.removeEventListener('foregroundWillDisplay', onForeground);
    };
  }, [loadActiveWalk]);

  const lastWalk = history[0];

  return (
    <div className="bg-white rounded-2xl p-6 border border-[#E5E7EB]" data-testid="walk-status-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-[#2C3E50] flex items-center gap-2">
          <Footprints className="w-5 h-5 text-[#4A7C59]" />
          Passeggiate
        </h3>
        <Button
          variant="ghost"
          onClick={() => setShowHistory((v) => !v)}
          className="text-[#4A7C59] text-sm"
          data-testid="walk-history-toggle"
        >
          Storico
          {showHistory ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
        </Button>
      </div>

      {isActive ? (
        <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-[#F0FDF4] border border-[#4A7C59]/20" data-testid="walk-status-running">
          <div className="min-w-0">
            <p className="font-semibold text-[#2C3E50] flex items-center gap-2">
              <PulsingDot />
              Passeggiata in corso
            </p>
            <p className="text-sm text-[#64748B] mt-0.5">
              {activeWalk.assistente_nome ? `con ${activeWalk.assistente_nome} · ` : ''}iniziata alle {formatTime(activeWalk.started_at)}
            </p>
          </div>
          <p className="text-2xl font-bold tabular-nums text-[#4A7C59]" data-testid="walk-status-timer">{elapsed}</p>
        </div>
      ) : null}
      {isActive && (
        <div className="mt-3">
          <WalkMap pins={runningPins} testId="walk-running-map" />
        </div>
      )}
      {isActive ? null : (
        <div data-testid="walk-status-idle">
          <p className="text-sm text-[#64748B]">Nessuna passeggiata in corso</p>
          <p className="text-xs text-[#9CA3AF] mt-1">
            {lastWalk ? `Ultima: ${walkSummary(lastWalk)}` : 'Nessuna passeggiata registrata'}
          </p>
        </div>
      )}

      {showHistory && (
        <div className="mt-4 border-t border-[#E5E7EB] pt-3" data-testid="walk-history-list">
          {history.length === 0 ? (
            <p className="text-sm text-[#9CA3AF]">Nessuna passeggiata registrata</p>
          ) : (
            history.map((item) => (
              <div key={item.id} className="border-b border-[#F3F4F6] last:border-0">
                <button
                  type="button"
                  onClick={() => setOpenWalkId((id) => (id === item.id ? null : item.id))}
                  className="w-full flex items-center justify-between gap-2 py-2 text-sm text-left hover:bg-[#F8FAFC] transition-colors"
                  data-testid={`walk-history-item-${item.id}`}
                >
                  <span className="text-[#2C3E50] capitalize">{formatDay(item.started_at)}</span>
                  <span className="text-[#64748B]">
                    {formatTime(item.started_at)} – {item.ended_at ? formatTime(item.ended_at) : '?'}
                  </span>
                  <span className={item.duration_minutes == null ? 'text-[#F59E0B]' : 'font-medium text-[#4A7C59]'}>
                    {item.duration_minutes == null ? 'Non terminata' : `${item.duration_minutes} min`}
                  </span>
                </button>
                {openWalkId === item.id && <WalkHistoryMap item={item} token={token} onPlacesSaved={savePlaces} />}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
